import Api from './Api';
import { errorHandler } from '../../utils';
import { DrugInterface } from '../../interfaces';
import { SearchDrugInCategory, SearchDrugInMultiCategory } from '../../interfaces/search';
import { SearchTypeEnum } from '../../enum';
import { isUndefined } from 'lodash';

class Drug extends Api {
  readonly urls = {
    all: '/Drugs/AllDrugs',
    save: '/Drugs/Save',
    get: '/Drugs/Detail/',
    remove: '/Drugs/Remove/',
    editInCategory: '/Drugs/EditInCategory',
    removeFromCategory: '/Drugs/RemoveFromCategory',
    types: '/Drugs/AllDrugTypes',
    suggest: '/Drugs/DrugSuggest',
  }

  save = async (data: DrugInterface): Promise<any> => {
    try {
      const result = await this.postFormData(this.urls.save, data)
      return result.data
    } catch (e) {
      errorHandler(e)
      return Promise.reject(e)
    }
  }

  suggest = async (data: DrugInterface): Promise<any> => {
    try {
      const result = await this.postFormData(this.urls.suggest, data)
      return result.data
    } catch (e) {
      errorHandler(e)
      return Promise.reject(e)
    }
  }

  all = async (skip: number = 0, top: number = 10): Promise<any> => {
    try {
      const result = await this.postJsonData(
        `${this.urls.all}?$top=${top}&$skip=${skip * top}&$orderby=id desc`,
      )
      return result.data
    } catch (e) {
      errorHandler(e)
      return Promise.reject(e)
    }
  }

  searchAll = async (
    term: string,
    page: number = 0,
    top: number = 20,
  ): Promise<{ items: any[]; count: number }> => {
    try {
      const q = term.trim().replace(/'/g, "''")
      const filter = q
        ? `&$filter=${encodeURIComponent(
          `contains(Name,'${q}') or contains(GenericName,'${q}') or contains(CodeGeneric,'${q}')`,
        )}`
        : ''
      const pageSize = Math.max(top, 1)
      const result = await this.postJsonData(
        `${this.urls.all}?$top=${pageSize}&$skip=${Math.max(page, 0) * pageSize}&$orderby=ID desc${filter}`,
      )
      const response = result.data
      const items = Array.isArray(response) ? response : response?.items ?? []
      const count = Number(response?.count ?? items.length)

      return {
        items,
        count: Number.isFinite(count) ? count : items.length,
      }
    } catch (e) {
      errorHandler(e)
      return Promise.reject(e)
    }
  }

  searchAllIds = async (term: string, top: number = 500): Promise<number[]> => {
    try {
      const q = term.trim().replace(/'/g, "''")
      const filter = q
        ? `&$filter=${encodeURIComponent(
          `contains(Name,'${q}') or contains(GenericName,'${q}') or contains(CodeGeneric,'${q}')`,
        )}`
        : ''
      const pageSize = Math.max(top, 1)
      const ids: number[] = []
      let skip = 0
      let totalCount = Number.POSITIVE_INFINITY

      while (skip < totalCount) {
        const result = await this.postJsonData(
          `${this.urls.all}?$top=${pageSize}&$skip=${skip}&$orderby=ID desc${filter}`,
        )
        const response = result.data
        const items = Array.isArray(response) ? response : response?.items ?? []
        const responseCount = Number(response?.count ?? items.length)

        totalCount = Number.isFinite(responseCount) ? responseCount : items.length
        items.forEach((item: any) => {
          const id = Number(item.id ?? item.ID)
          if (Number.isFinite(id)) ids.push(id)
        })

        if (items.length === 0) break
        skip += items.length
      }

      return Array.from(new Set(ids))
    } catch (e) {
      errorHandler(e)
      return Promise.reject(e)
    }
  }

  allByCategory = async (
    categoryId: number | string,
    page: number = 0,
    top: number = 10,
  ): Promise<{ items: any[]; count: number }> => {
    try {
      const normalizedCategoryId = Number(categoryId)
      if (!Number.isFinite(normalizedCategoryId)) {
        return { items: [], count: 0 }
      }

      const pageSize = Math.max(top, 1)
      const filter = encodeURIComponent(`Category/ID eq ${normalizedCategoryId}`)
      const result = await this.postJsonData(
        `${this.urls.all}?$filter=${filter}&$top=${pageSize}&$skip=${Math.max(page, 0) * pageSize}&$orderby=ID desc`,
      )
      const response = result.data
      const items = Array.isArray(response) ? response : response?.items ?? []
      const responseTotal = response?.count ?? response?.totalCount ?? response?.total
      const count = Number(responseTotal ?? items.length)

      return {
        items,
        count: Number.isFinite(count) ? count : items.length,
      }
    } catch (e) {
      errorHandler(e)
      return Promise.reject(e)
    }
  }

  get = async (id: number | string): Promise<any> => {
    try {
      const result = await this.postJsonData(`${this.urls.get}${id}`)
      return result.data
    } catch (e) {
      errorHandler(e)
      return Promise.reject(e)
    }
  }

  remove = async (id: number | string): Promise<any> => {
    try {
      const result = await this.postJsonData(`${this.urls.remove}${id}`)
      return result.data
    } catch (e) {
      errorHandler(e)
      return Promise.reject(e)
    }
  }

  editInCategory = async (data: {
    drugID: number
    categoryID: number
  }): Promise<any> => {
    try {
      const result = await this.postJsonData(this.urls.editInCategory, data)
      return result.data
    } catch (e) {
      errorHandler(e)
      return Promise.reject(e)
    }
  }

  removeFromCategory = async (id: number | string): Promise<any> => {
    try {
      const result = await this.postData(
        `${this.urls.removeFromCategory}?id=${encodeURIComponent(id)}`,
      )
      return result.data
    } catch (e) {
      errorHandler(e)
      return Promise.reject(e)
    }
  }

  types = async (): Promise<any> => {
    try {
      const result = await this.postJsonData(this.urls.types)
      return result.data
    } catch (e) {
      errorHandler(e)
      return Promise.reject(e)
    }
  }

  searchMedicalDrug = async (
    name: string,
    searchType: string = '',
    count = 100,
  ): Promise<any> => {
    const result = await this.getData(
      `/Search/SearchMedicalDrug?name=${name}&searchType=${searchType}&count=${count}`,
    )
    return result.data
  }

  seerchDrugInCategory = async (data: SearchDrugInCategory): Promise<any> => {
    let queryString = `/Search/SearchDrugInCategory?name=${
      data.name ?? ''
    }&searchType=${data.searchType ?? SearchTypeEnum.CONTAINS}&count=${
      data.count ?? 99
    }`
    if (!isUndefined(data.categoryId)) {
      queryString += `&categoryId=${data.categoryId}`
    }
    const result = await this.getData(queryString)

    return result.data
  }

  searchDrugInMultipleCategory = async (
    data: SearchDrugInMultiCategory,
  ): Promise<any> => {
    let queryString = `/Search/SearchDrugInMultiCategory?name=${
      data.name ?? ''
    }&searchType=${data.searchType ?? SearchTypeEnum.CONTAINS}&count=${
      data.count ?? 99
    }`
    if (!isUndefined(data.categoryId)) {
      queryString += `&categoryId1=${data.categoryId}`
    }
    queryString += `${
      !!data.secondCategory ? `&categoryId2=${data.secondCategory}` : ''
    }${!!data.thirdCategory ? `&categoryId3=${data.thirdCategory}` : ''}`

    const result = await this.postData(queryString)
    return result.data
  }

  assignCategory = async (data: { drugIds: number[]; categoryID: number }): Promise<any> => {
    try {
      const result = await this.postJsonData('/Drugs/AssignCategory', data)
      return result.data
    } catch (e) {
      errorHandler(e)
      return Promise.reject(e)
    }
  }
}

export default Drug
